import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { FileHistoryModal as FileHistoryList } from '@l2u/ui/components/FileHistoryModal.tsx';
import type { AdoptionHistoryItem, FileHistoryItem } from '@l2u/ui/lib/fileHistory.ts';
import type { SubmissionComment } from '@l2u/ui/lib/types.ts';

// Admin wrapper around the shared history modal: history (including superseded
// rows + their download URLs) comes from the admin file-history endpoint. The
// vendor app renders the same modal from GET /api/me/.../files/history.
//
// For a Documents-tab document with recorded approvals (`approvals` non-null,
// adminDocumentsTabApprovals) it also loads the comment thread (the same query
// as CommentModal) so History shows the approval markers and the earlier
// conversations. Unpublished drafts land in the current conversation, which
// History never shows. `approvals === null` is the plain file list, as before,
// with no comments query.
export function FileHistoryModal({
  companyId,
  submissionId,
  questionLabel,
  approvals,
  onClose,
}: {
  companyId: string;
  submissionId: string;
  questionLabel: string;
  /** The document's approval moments on its LIVE status (adminDocumentsTabApprovals); null = the plain file list. */
  approvals: string[] | null;
  onClose: () => void;
}) {
  const historyQuery = useQuery({
    queryKey: ['company', companyId, 'submission', submissionId, 'file-history'],
    queryFn: () =>
      adminApi.get<{
        files: FileHistoryItem[];
        adoptions: AdoptionHistoryItem[];
      }>(`/api/companies/${companyId}/submissions/${submissionId}/files/history`),
  });

  const commentsQuery = useQuery({
    queryKey: ['company', companyId, 'submissions', submissionId, 'comments'],
    queryFn: () =>
      adminApi.get<{ comments: SubmissionComment[] }>(
        `/api/companies/${companyId}/submissions/${submissionId}/comments`,
      ),
    enabled: approvals !== null,
    retry: 1,
  });

  // A disabled query reports isLoading false, so null approvals never waits on it.
  const loading = historyQuery.isLoading || commentsQuery.isLoading;
  const error = historyQuery.isError
    ? historyQuery.error
    : commentsQuery.isError
      ? commentsQuery.error
      : null;

  return (
    <FileHistoryList
      questionLabel={questionLabel}
      loading={loading}
      error={error}
      files={historyQuery.data?.files ?? []}
      adoptions={historyQuery.data?.adoptions ?? []}
      onClose={onClose}
      {...(approvals !== null
        ? {
            thread: {
              comments: commentsQuery.data?.comments ?? [],
              approvals,
              originalNote:
                'The conversation from the original review is on the Questionnaire tab.',
            },
          }
        : {})}
    />
  );
}
